import { createFileRoute } from "@tanstack/react-router";
import { getIndexingOverview, type IndexStatusSummary, type IndexStatusRow } from "@/lib/indexing-status.functions";

const BASE_URL = "https://lehitradessaeo.com";

export const Route = createFileRoute("/admin/indexing")({
  head: () => ({
    meta: [
      { title: "Indexing Status | Admin" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "description", content: "Internal Google indexing tracker." },
    ],
    links: [{ rel: "canonical", href: `${BASE_URL}/admin/indexing` }],
  }),
  loader: () => getIndexingOverview(),
  component: IndexingDashboard,
});

function fmt(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-US", { timeZone: "UTC" }) + " UTC";
}

function IndexingDashboard() {
  const s = Route.useLoaderData() as IndexStatusSummary;
  const pct = (n: number) => (s.total ? Math.round((n / s.total) * 1000) / 10 : 0);

  const cards = [
    { label: "Total tracked", value: s.total, tone: "bg-muted" },
    { label: "Indexed", value: s.indexed, sub: `${pct(s.indexed)}%`, tone: "bg-green-500/10 text-green-700 dark:text-green-300" },
    { label: "Crawled – not indexed", value: s.crawledNotIndexed, sub: `${pct(s.crawledNotIndexed)}%`, tone: "bg-amber-500/10 text-amber-700 dark:text-amber-300" },
    { label: "Discovered – not indexed", value: s.discoveredNotIndexed, sub: `${pct(s.discoveredNotIndexed)}%`, tone: "bg-blue-500/10 text-blue-700 dark:text-blue-300" },
    { label: "Excluded", value: s.excluded, sub: `${pct(s.excluded)}%`, tone: "bg-muted" },
    { label: "Errors", value: s.error, sub: `${pct(s.error)}%`, tone: "bg-red-500/10 text-red-700 dark:text-red-300" },
  ];

  return (
    <div className="container mx-auto max-w-6xl px-4 py-10 space-y-8">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight">Google indexing status</h1>
        <p className="text-sm text-muted-foreground">
          Live view of URL Inspection results for every page in the sitemap. Last snapshot: {fmt(s.lastCheckedAt)}.
        </p>
        <div className="flex flex-wrap gap-2 text-sm">
          <a
            href="/api/public/hooks/check-indexing?limit=40"
            target="_blank"
            rel="noopener"
            className="rounded-md border px-3 py-1.5 hover:bg-accent"
          >
            Run a batch (40 URLs)
          </a>
          <a
            href="/api/public/hooks/check-indexing?limit=200"
            target="_blank"
            rel="noopener"
            className="rounded-md border px-3 py-1.5 hover:bg-accent"
          >
            Run larger batch (200)
          </a>
        </div>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map((c) => (
          <div key={c.label} className={`rounded-lg p-4 ${c.tone}`}>
            <div className="text-xs uppercase tracking-wide opacity-70">{c.label}</div>
            <div className="mt-1 text-2xl font-semibold">{c.value.toLocaleString()}</div>
            {c.sub && <div className="text-xs opacity-70">{c.sub}</div>}
          </div>
        ))}
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3">Coverage breakdown</h2>
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-3 py-2">State</th>
                <th className="px-3 py-2 text-right">URLs</th>
                <th className="px-3 py-2 text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {s.byState.map((r: { state: string; count: number }) => (
                <tr key={r.state} className="border-t">
                  <td className="px-3 py-2">{r.state}</td>
                  <td className="px-3 py-2 text-right">{r.count.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{pct(r.count)}%</td>
                </tr>
              ))}
              {s.byState.length === 0 && (
                <tr>
                  <td className="px-3 py-4 text-muted-foreground" colSpan={3}>
                    No inspections yet. Trigger a batch above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3">Recently indexed pages</h2>
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-3 py-2">URL</th>
                <th className="px-3 py-2">Coverage</th>
                <th className="px-3 py-2">First indexed</th>
              </tr>
            </thead>
            <tbody>
              {s.recentlyDiscovered.map((r: IndexStatusRow) => (
                <tr key={r.url} className="border-t">
                  <td className="px-3 py-2 truncate max-w-[420px]">
                    <a className="underline hover:no-underline" href={r.url} target="_blank" rel="noopener">
                      {r.url}
                    </a>
                  </td>
                  <td className="px-3 py-2">{r.coverage_state ?? "—"}</td>
                  <td className="px-3 py-2">{fmt(r.first_indexed_at)}</td>
                </tr>
              ))}
              {s.recentlyDiscovered.length === 0 && (
                <tr>
                  <td className="px-3 py-4 text-muted-foreground" colSpan={3}>
                    None discovered as indexed yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3">Latest inspections</h2>
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-3 py-2">URL</th>
                <th className="px-3 py-2">Coverage</th>
                <th className="px-3 py-2">Verdict</th>
                <th className="px-3 py-2">Last crawl</th>
                <th className="px-3 py-2">Checked</th>
              </tr>
            </thead>
            <tbody>
              {s.recent.map((r: IndexStatusRow) => (
                <tr key={r.url} className="border-t">
                  <td className="px-3 py-2 truncate max-w-[360px]">
                    <a className="underline hover:no-underline" href={r.url} target="_blank" rel="noopener">
                      {r.url}
                    </a>
                  </td>
                  <td className="px-3 py-2">{r.coverage_state ?? "—"}</td>
                  <td className="px-3 py-2">{r.verdict ?? "—"}</td>
                  <td className="px-3 py-2">{fmt(r.last_crawl_time)}</td>
                  <td className="px-3 py-2">{fmt(r.last_checked_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}